import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MUITextField from '@mui/material/TextField';
import MUIButton from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { useLoading, useSnackbar } from 'contexts';
import { LoginSchema } from 'schemas';
import { signIn } from 'services';
import Session from 'utils/Session';

const PrivacyPolicy = "https://assets.languify.in/api-docs/Languify-Privacy-Policy.pdf";
const InitialValues = { email: '', password: '' };

// ─── Login Header ────────────────────────────────────────────────────────────

function LoginHeader() {
    const [searchParams] = useSearchParams();

    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                backgroundColor: theme.palette.shades['clr-white-900'],
                borderBottom: `1px solid ${theme.palette.neutral['clr-200']}`,
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 40px',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1100,
                [theme.breakpoints.down('md')]: {
                    padding: '0 16px',
                },
            })}
        >
            {/* Logo */}
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: '#0284C5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="body01-bold" sx={{ color: '#FFFFFF', fontSize: '14px' }}>
                        IB
                    </Typography>
                </Box>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography component="span" variant="body01-bold" sx={{ color: '#0284C5' }}>
                            IELTS
                        </Typography>
                        &nbsp;
                        <Typography component="span" variant="body01-bold" color="neutral.clr-900">
                            BandBoost
                        </Typography>
                    </Box>
                    <Typography variant="body02-regular" sx={{ color: '#0284C5', fontSize: '11px', lineHeight: 1 }}>
                        by languify
                    </Typography>
                </Box>
            </Link>

        </Box>
    );
}

// ─── Shared input field style ─────────────────────────────────────────────────

const inputSx = (theme) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: theme.palette.shades['clr-white-900'],
        ...theme.typography['body02-regular'],
        '& fieldset': { borderColor: theme.palette.neutral['clr-200'] },
        '&:hover fieldset': { borderColor: theme.palette.neutral['clr-300'] },
        '&.Mui-focused fieldset': { borderColor: '#0284C5', borderWidth: '1.5px' },
        '&.Mui-error fieldset': { borderColor: theme.palette.danger?.main || '#E2483C' },
    },
    '& input': {
        padding: '11px 14px 11px 0',
        color: theme.palette.neutral['clr-900'],
        ...theme.typography['body02-regular'],
        '&::placeholder': { color: theme.palette.neutral['clr-400'], opacity: 1 },
    },
});

// ─── Main component ───────────────────────────────────────────────────────────

function Login() {
    const loading = useLoading();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [showPassword, setShowPassword] = useState(false);
    const [values, setValues] = useState(InitialValues);
    const [errors, setErrors] = useState({});

    const handleLogin = async (e) => {
        e.preventDefault();
        setErrors({});

        const formData = new FormData(e.target);
        const vals = {};
        formData.forEach((value, key) => { vals[key] = value; });

        const { error, value } = LoginSchema.validate(vals, { abortEarly: false });

        if (error) {
            setErrors(error.details.reduce((acc, curr) => {
                acc[curr.path.toString()] = true;
                return acc;
            }, {}));
            return;
        }

        try {
            loading.show();
            await signIn({ email: value.email, password: value.password });
            snackbar.success("Logged in successfully");
            navigate("/practice");
        } catch (error) {
            console.error(error);
            snackbar.error(error.message || "Uh Oh! Something went wrong!");
        } finally {
            loading.hide();
        }
    };

    React.useEffect(() => {
        if (Session.isLoggedIn()) navigate("/practice");
    }, []);

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <LoginHeader />

            <Box
                sx={(theme) => ({
                    flex: 1,
                    backgroundColor: theme.palette.shades['clr-offWhite'],
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: '108px',
                    pb: '48px',
                    px: '24px',
                })}
            >
                <Box
                    component="form"
                    onSubmit={handleLogin}
                    sx={(theme) => ({
                        width: '418px',
                        backgroundColor: theme.palette.shades['clr-white-900'],
                        borderRadius: '16px',
                        border: `1px solid ${theme.palette.neutral['clr-200']}`,
                        padding: '40px',
                    })}
                >
                    <Typography
                        component="h1"
                        variant="h4-bold"
                        color="neutral.clr-900"
                        sx={{ mb: '10px' }}
                    >
                        Sign In
                    </Typography>
                    <Typography
                        component="p"
                        variant="body01-regular"
                        color="neutral.clr-500"
                        sx={{ lineHeight: 1.5, mb: '32px' }}
                    >
                        Welcome back to IELTS BandBoost. Ready to level up your score?
                    </Typography>

                    {/* Email field */}
                    <Box sx={{ mb: '20px' }}>
                        <Typography
                            component="label"
                            variant="body02-semiBold"
                            color="neutral.clr-700"
                            sx={{ display: 'block', mb: '8px' }}
                        >
                            Email Address
                        </Typography>
                        <MUITextField
                            fullWidth
                            name="email"
                            type="email"
                            placeholder="name@example.com"
                            value={values.email}
                            error={!!errors.email}
                            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
                            inputProps={{ autoComplete: 'email' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlinedIcon sx={{ color: 'neutral.clr-400', fontSize: '1.1rem' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={inputSx}
                        />
                    </Box>

                    {/* Password field */}
                    <Box sx={{ mb: '20px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
                            <Typography component="label" variant="body02-semiBold" color="neutral.clr-700">
                                Password
                            </Typography>
                            <Typography
                                variant="body02-semiBold"
                                sx={{
                                    color: '#0284C5',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Forgot password?
                            </Typography>
                        </Box>
                        <MUITextField
                            fullWidth
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={values.password}
                            error={!!errors.password}
                            onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
                            inputProps={{ autoComplete: 'current-password' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon sx={{ color: 'neutral.clr-400', fontSize: '1.1rem' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowPassword((v) => !v)}
                                            edge="end"
                                            sx={{ color: 'neutral.clr-400' }}
                                        >
                                            {showPassword
                                                ? <VisibilityOffOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                                : <VisibilityOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={(theme) => ({
                                ...inputSx(theme),
                                '& input': {
                                    padding: '11px 0',
                                    color: theme.palette.neutral['clr-900'],
                                    ...theme.typography['body02-regular'],
                                    '&::placeholder': { color: theme.palette.neutral['clr-400'], opacity: 1 },
                                },
                            })}
                        />
                    </Box>

                    {/* Sign In button */}
                    <MUIButton
                        id="login-button"
                        type="submit"
                        fullWidth
                        variant="contained"
                        disableElevation
                        sx={(theme) => ({
                            backgroundColor: '#0284C5',
                            color: theme.palette.shades['clr-white-900'],
                            borderRadius: '8px',
                            padding: '13px',
                            textTransform: 'none',
                            ...theme.typography['body01-bold'],
                            mb: '24px',
                            '&:hover': { backgroundColor: '#026BA1' },
                        })}
                    >
                        Sign In
                    </MUIButton>

                    {/* Sign up link */}
                    <Typography
                        component="p"
                        variant="body02-regular"
                        color="neutral.clr-500"
                        sx={{ textAlign: 'center' }}
                    >
                        Don't have an account?{' '}
                        <Typography
                            component={Link}
                            to={`/register?aid=${searchParams.get("aid") || ''}`}
                            variant="body02-semiBold"
                            sx={{ color: '#0284C5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Sign up for free
                        </Typography>
                    </Typography>
                </Box>

                {/* Below-card section */}
                <Box sx={{ width: '418px', mt: '20px' }}>
                    {/* Terms */}
                    <Typography
                        component="p"
                        variant="body02-regular"
                        color="neutral.clr-500"
                        sx={{ textAlign: 'center', lineHeight: 1.6, mb: '24px' }}
                    >
                        By signing in, you agree to our{' '}
                        <Typography
                            component="a"
                            href={PrivacyPolicy}
                            target="_blank"
                            rel="noreferrer"
                            variant="body02-regular"
                            sx={{ color: '#0284C5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Terms of Service
                        </Typography>
                        {' '}and{' '}
                        <Typography
                            component="a"
                            href={PrivacyPolicy}
                            target="_blank"
                            rel="noreferrer"
                            variant="body02-regular"
                            sx={{ color: '#0284C5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Privacy Policy
                        </Typography>
                    </Typography>

                    {/* Stats row */}
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.shades['clr-white-900'],
                            borderRadius: '12px',
                            border: `1px solid ${theme.palette.neutral['clr-200']}`,
                            overflow: 'hidden',
                        })}
                    >
                        <Box sx={{ flex: 1, textAlign: 'center', py: '16px' }}>
                            <Typography component="div" variant="h5-bold" color="neutral.clr-900">
                                50k+
                            </Typography>
                            <Typography component="div" variant="overline-semiBold" color="neutral.clr-400">
                                Active Students
                            </Typography>
                        </Box>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={(theme) => ({ borderColor: theme.palette.neutral['clr-200'] })}
                        />

                        <Box sx={{ flex: 1, textAlign: 'center', py: '16px' }}>
                            <Typography component="div" variant="h5-bold" color="neutral.clr-900">
                                8.5+
                            </Typography>
                            <Typography component="div" variant="overline-semiBold" color="neutral.clr-400">
                                Avg Band Score
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default Login;
